
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

// ---------------------------------------------------------
// Helpers
// ---------------------------------------------------------

const toISODate = (value) => {
  if (!value) return null;

  const str = String(value).trim();

  // YYYY-MM-DD or ISO timestamp
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  // DD-MMM-YYYY
  const match = str.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
  if (!match) return null;

  const months = {
    jan: "01", feb: "02", mar: "03",
    apr: "04", may: "05", jun: "06",
    jul: "07", aug: "08", sep: "09",
    oct: "10", nov: "11", dec: "12",
  };

  const month = months[match[2].toLowerCase()];
  if (!month) return null;

  return `${match[3]}-${month}-${match[1].padStart(2, "0")}`;
};

const getIndustries = (industry) => {
  if (!industry) return [];

  return [...new Set(
    String(industry)
      .split("/")
      .map(x => x.trim())
      .filter(x => x && !["N", "NEW"].includes(x.toUpperCase()))
  )];
};

const getPrevDateKey = (keys, current, offset = 1) => {
  const index = keys.indexOf(current);
  return index >= offset ? keys[index - offset] : null;
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const pct = (curr, prev) => {
  const c = toNumber(curr);
  const p = toNumber(prev);

  if (c === null || p === null || p === 0) return null;

  return ((c - p) / p) * 100;
};

const formatInCrores = (value) => {
  const num = toNumber(value);
  return num === null ? "—" : `₹${(num / 1e7).toFixed(2)} Cr`;
};

const getChange = (curr, prev) => {
  const change = pct(curr, prev);

  if (change === null) return "—";

  return (
    <span
      className={`font-semibold ${
        change >= 0 ? "text-green-600" : "text-red-600"
      }`}
    >
      {change.toFixed(2)}%
    </span>
  );
};

const normalizeFinancialData = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }

  const result = {};

  Object.entries(raw).forEach(([key, value]) => {
    const date = toISODate(key);
    if (date) result[date] = toNumber(value);
  });

  return result;
};

// ---------------------------------------------------------
// Main Component
// ---------------------------------------------------------

function Results() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sortConfig, setSortConfig] = useState({
    key: "creation_Date",
    direction: "desc",
  });

  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [industryFilterOpen, setIndustryFilterOpen] = useState(false);
  const [industrySearch, setIndustrySearch] = useState("");

  const [selectedDateFilter, setSelectedDateFilter] = useState(null);

  const industryFilterRef = useRef(null);

  // ---------------------------------------------------------
  // Fetch data
  // ---------------------------------------------------------

  useEffect(() => {
    let active = true;

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/integratedResults`)
      .then(res => {
        if (active) {
          setData(Array.isArray(res.data) ? res.data : []);
        }
      })
      .catch(err => {
        console.error("Failed to fetch results", err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  // ---------------------------------------------------------
  // Close industry dropdown on outside click
  // ---------------------------------------------------------

  useEffect(() => {
    if (!industryFilterOpen) return;

    const handleOutsideClick = (event) => {
      if (
        industryFilterRef.current &&
        !industryFilterRef.current.contains(event.target)
      ) {
        setIndustryFilterOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIndustryFilterOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [industryFilterOpen]);

  // ---------------------------------------------------------
  // Only actual financial results
  // ---------------------------------------------------------

  const financialResults = useMemo(() => {
    return data.filter(
      row => row.type === "Integrated Filing- Financials"
    );
  }, [data]);

  // ---------------------------------------------------------
  // Available dates (newest first)
  // ---------------------------------------------------------

  const uniqueDates = useMemo(() => {
    return [
      ...new Set(
        financialResults
          .map(row => toISODate(row.creation_Date))
          .filter(Boolean)
      ),
    ].sort((a, b) => b.localeCompare(a));
  }, [financialResults]);

  // ---------------------------------------------------------
  // Apply date filter first
  // ---------------------------------------------------------

  const dateFilteredData = useMemo(() => {
    if (!selectedDateFilter) return financialResults;

    return financialResults.filter(
      row => toISODate(row.creation_Date) === selectedDateFilter
    );
  }, [financialResults, selectedDateFilter]);

  // ---------------------------------------------------------
  // Industry counts based on available results
  // ---------------------------------------------------------

  const industryCounts = useMemo(() => {
    const counts = {};

    dateFilteredData.forEach(row => {
      getIndustries(row.industry).forEach(industry => {
        counts[industry] = (counts[industry] || 0) + 1;
      });
    });

    return counts;
  }, [dateFilteredData]);

  const allIndustryOptions = useMemo(() => {
    // Keep already selected industries visible, even when
    // a new date has no matching records for them.
    return [
      ...new Set([
        ...Object.keys(industryCounts),
        ...selectedIndustries,
      ]),
    ].sort((a, b) => a.localeCompare(b));
  }, [industryCounts, selectedIndustries]);

  const filteredIndustryOptions = useMemo(() => {
    const search = industrySearch.trim().toLowerCase();

    return allIndustryOptions.filter(industry =>
      industry.toLowerCase().includes(search)
    );
  }, [allIndustryOptions, industrySearch]);

  // ---------------------------------------------------------
  // Industry selection
  // ---------------------------------------------------------

  const toggleIndustry = (industry) => {
    setSelectedIndustries(prev =>
      prev.includes(industry)
        ? prev.filter(x => x !== industry)
        : [...prev, industry]
    );
  };

  const selectAllIndustries = () => {
    setSelectedIndustries(prev => [
      ...new Set([...prev, ...filteredIndustryOptions]),
    ]);
  };

  const clearIndustries = () => {
    setSelectedIndustries([]);
  };

  // ---------------------------------------------------------
  // Apply industry filter
  // ---------------------------------------------------------

  const filteredData = useMemo(() => {
    if (!selectedIndustries.length) return dateFilteredData;

    return dateFilteredData.filter(row => {
      const industries = getIndustries(row.industry);

      return selectedIndustries.some(industry =>
        industries.includes(industry)
      );
    });
  }, [dateFilteredData, selectedIndustries]);

  // ---------------------------------------------------------
  // Enrich financial results
  // ---------------------------------------------------------

  const enrichedData = useMemo(() => {
    return filteredData.map(row => {
      const isConsolidated =
        String(row.consolidated || "").toLowerCase() === "consolidated";

      const revenueRaw = isConsolidated
        ? row.last5revenue_consolidated
        : row.last5revenue_standalone;

      const patRaw = isConsolidated
        ? row.last5PAT_consolidated
        : row.last5PAT_standalone;

      const revenueData = normalizeFinancialData(revenueRaw);
      const patData = normalizeFinancialData(patRaw);

      const dateKeys = Object.keys(revenueData).sort();
      const currISO = toISODate(row.qe_Date);

      const prevQKey = getPrevDateKey(dateKeys, currISO, 1);
      const prevYKey = getPrevDateKey(dateKeys, currISO, 4);

      const revCurr = revenueData[currISO];
      const revPrevQ = revenueData[prevQKey];
      const revPrevY = revenueData[prevYKey];

      const patCurr = patData[currISO];
      const patPrevQ = patData[prevQKey];
      const patPrevY = patData[prevYKey];

      return {
        ...row,

        revCurr,
        revPrevQ,
        revPrevY,

        patCurr,
        patPrevQ,
        patPrevY,

        revQQ: pct(revCurr, revPrevQ),
        revYY: pct(revCurr, revPrevY),

        patQQ: pct(patCurr, patPrevQ),
        patYY: pct(patCurr, patPrevY),

        change: pct(row.currentPrice, row.previousClose),
      };
    });
  }, [filteredData]);

  // ---------------------------------------------------------
  // Sorting
  // ---------------------------------------------------------

  const sortedData = useMemo(() => {
    const { key, direction } = sortConfig;
    const dir = direction === "asc" ? 1 : -1;

    return [...enrichedData].sort((a, b) => {
      if (key === "creation_Date") {
        const dateA = toISODate(a.creation_Date) || "";
        const dateB = toISODate(b.creation_Date) || "";

        return dir * dateA.localeCompare(dateB);
      }

      const valA = a[key];
      const valB = b[key];

      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === "string" || typeof valB === "string") {
        return dir * String(valA).localeCompare(String(valB));
      }

      return dir * (valA - valB);
    });
  }, [enrichedData, sortConfig]);

  const renderSortableHeader = (label, key) => (
    <th
      className="p-2 cursor-pointer select-none text-blue-600 whitespace-nowrap"
      onClick={() =>
        setSortConfig(prev => ({
          key,
          direction:
            prev.key === key && prev.direction === "asc"
              ? "desc"
              : "asc",
        }))
      }
    >
      {label}
      {sortConfig.key === key
        ? sortConfig.direction === "asc"
          ? " ↑"
          : " ↓"
        : ""}
    </th>
  );

  // ---------------------------------------------------------
  // Render industry labels
  // ---------------------------------------------------------

  const renderIndustry = (industry) => {
    const industries = getIndustries(industry);

    if (!industries.length) return "—";

    return (
      <div className="flex flex-wrap gap-1">
        {industries.map(name => (
          <span
            key={name}
            className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full"
          >
            {name}
          </span>
        ))}
      </div>
    );
  };

  // ---------------------------------------------------------
  // Financial change cell
  // ---------------------------------------------------------

  const renderFinancialChange = (curr, prev) => (
    <>
      {getChange(curr, prev)}

      {curr != null && prev != null && (
        <div className="text-xs text-gray-500 whitespace-nowrap">
          {formatInCrores(curr)}
          {" Vs "}
          {formatInCrores(prev)}
        </div>
      )}
    </>
  );

  // ---------------------------------------------------------
  // Loading
  // ---------------------------------------------------------

  if (loading) {
    return (
      <div className="text-center text-blue-600 font-medium py-6 animate-pulse">
        Loading...
      </div>
    );
  }

  // ---------------------------------------------------------
  // Render
  // ---------------------------------------------------------

  return (
    <div className="p-4 mb-6">

      {/* Information */}

      <div className="text-gray-500 text-sm">
        Updates daily 11 PM.
      </div>

      <span className="text-xs text-gray-500 font-normal">
        *Some results data might be inaccurate, we are working on this.
      </span>

      {/* ----------------------------------------------------- */}
      {/* Industry Filter */}
      {/* ----------------------------------------------------- */}

      <div className="mt-3 mb-3 flex items-center gap-2">

        <span className="text-sm text-gray-600 whitespace-nowrap">
          Filter Industry:
        </span>

        <div className="relative" ref={industryFilterRef}>

          <button
            type="button"
            onClick={() => setIndustryFilterOpen(prev => !prev)}
            aria-expanded={industryFilterOpen}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-full hover:bg-gray-100 hover:border-gray-300 transition"
          >
            <span>
              {selectedIndustries.length > 0
                ? `${selectedIndustries.length} selected`
                : "Select Industry"}
            </span>

            <span className="text-[10px] text-gray-400">
              {industryFilterOpen ? "▲" : "▼"}
            </span>
          </button>

          {industryFilterOpen && (
            <div className="absolute left-0 top-full mt-2 w-[290px] max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">

              {/* Search */}

              <div className="p-2.5 border-b border-gray-100">
                <input
                  type="text"
                  value={industrySearch}
                  onChange={e => setIndustrySearch(e.target.value)}
                  placeholder="Search industry..."
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none focus:bg-white focus:border-blue-400"
                />
              </div>

              {/* Actions */}

              <div className="flex justify-between items-center px-3 py-2 border-b border-gray-100">

                <button
                  type="button"
                  onClick={selectAllIndustries}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                  Select All
                </button>

                <button
                  type="button"
                  onClick={clearIndustries}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>

              </div>

              {/* Industry Options */}

              <div className="max-h-64 overflow-y-auto py-1">

                {filteredIndustryOptions.map(industry => {
                  const selected = selectedIndustries.includes(industry);
                  const count = industryCounts[industry] || 0;

                  return (
                    <label
                      key={industry}
                      className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${
                        selected
                          ? "bg-blue-50"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleIndustry(industry)}
                        className="h-4 w-4 rounded border-gray-300"
                      />

                      <span className="truncate text-gray-700 flex-1">
                        {industry}
                      </span>

                      <span className="text-xs text-gray-400 tabular-nums ml-auto">
                        ({count})
                      </span>

                    </label>
                  );
                })}

                {filteredIndustryOptions.length === 0 && (
                  <div className="px-3 py-4 text-sm text-gray-400 text-center">
                    No industries found
                  </div>
                )}

              </div>

            </div>
          )}

        </div>
      </div>

      {/* ----------------------------------------------------- */}
      {/* Date Filter */}
      {/* ----------------------------------------------------- */}

      {uniqueDates.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-2 mb-3">

          <span className="text-sm text-gray-600 mr-1">
            Date:
          </span>

          {uniqueDates.map(dateStr => (
            <button
              key={dateStr}
              type="button"
              onClick={() =>
                setSelectedDateFilter(prev =>
                  prev === dateStr ? null : dateStr
                )
              }
              className={`px-3 py-1 text-xs rounded-full border ${
                selectedDateFilter === dateStr
                  ? "bg-yellow-300 font-semibold"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
            >
              {new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-IN")}
            </button>
          ))}

          {selectedDateFilter && (
            <button
              type="button"
              onClick={() => setSelectedDateFilter(null)}
              className="px-2 py-1 text-xs border border-red-400 rounded-full bg-white text-red-600 ml-2"
            >
              Clear Date
            </button>
          )}

        </div>
      )}

      {/* ----------------------------------------------------- */}
      {/* Row Count */}
      {/* ----------------------------------------------------- */}

      <div className="text-xs text-gray-500 mb-2">
        Total rows: {sortedData.length}
      </div>

      {/* ----------------------------------------------------- */}
      {/* Results Table */}
      {/* ----------------------------------------------------- */}

      <div className="overflow-x-auto relative">

        <table className="table-auto border-collapse w-full text-sm text-gray-800 font-normal">

          <thead className="bg-gray-200 text-left sticky top-0 z-40">

            <tr>

              <th className="p-2 sticky left-0 bg-gray-200 z-50">
                Company
              </th>

              <th className="p-2 text-left">
                Industry
              </th>

              <th className="p-2">
                Quarter End
              </th>

              {renderSortableHeader("Date", "creation_Date")}

              <th className="p-2">
                Consolidated
              </th>

              {renderSortableHeader("Change", "change")}

              {renderSortableHeader("Revenue Q-Q%", "revQQ")}

              {renderSortableHeader("Revenue Y-Y%", "revYY")}

              {renderSortableHeader("PAT Q-Q%", "patQQ")}

              {renderSortableHeader("PAT Y-Y%", "patYY")}

              <th className="p-2">
                XBRL
              </th>

            </tr>

          </thead>

          <tbody>

            {sortedData.length === 0 ? (

              <tr>
                <td
                  colSpan={11}
                  className="text-center text-gray-500 py-4"
                >
                  No matching results found.
                </td>
              </tr>

            ) : (

              sortedData.map((row, idx) => (

                <tr
                  key={`${row.symbol}-${row.creation_Date}-${row.qe_Date}-${row.consolidated}-${idx}`}
                  className={
                    idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }
                >

                  {/* Company */}

                  <td className="p-2 sticky left-0 bg-inherit z-10 font-medium">

                    <a
                      href={`/symbol/${row.symbol}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      {row.cmName || "—"}
                    </a>

                  </td>

                  {/* Industry */}

                  <td className="p-2">
                    {renderIndustry(row.industry)}
                  </td>

                  {/* Quarter End */}

                  <td className="p-2">
                    {row.qe_Date || "—"}
                  </td>

                  {/* Filing Date */}

                  <td className="p-2">
                    {row.creation_Date || "—"}
                  </td>

                  {/* Consolidated */}

                  <td className="p-2">
                    {row.consolidated || "—"}
                  </td>

                  {/* Price Change */}

                  <td className="p-2">
                    {getChange(
                      row.currentPrice,
                      row.previousClose
                    )}
                  </td>

                  {/* Revenue Q-Q */}

                  <td className="p-2">
                    {renderFinancialChange(
                      row.revCurr,
                      row.revPrevQ
                    )}
                  </td>

                  {/* Revenue Y-Y */}

                  <td className="p-2">
                    {renderFinancialChange(
                      row.revCurr,
                      row.revPrevY
                    )}
                  </td>

                  {/* PAT Q-Q */}

                  <td className="p-2">
                    {renderFinancialChange(
                      row.patCurr,
                      row.patPrevQ
                    )}
                  </td>

                  {/* PAT Y-Y */}

                  <td className="p-2">
                    {renderFinancialChange(
                      row.patCurr,
                      row.patPrevY
                    )}
                  </td>

                  {/* XBRL */}

                  <td className="p-2">

                    {row.xbrl ? (
                      <a
                        href={row.xbrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        View
                      </a>
                    ) : (
                      "—"
                    )}

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

    </div>
  );
}

export default Results;
